import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { pagosAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'https://lossaliasfutbolclub.com/api';

const estadoConfig = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: '⏳' },
  confirmado: { label: 'Confirmado', color: 'bg-green-100 text-green-800 border-green-300', icon: '✅' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-800 border-red-300', icon: '❌' },
};

const conceptoLabels = {
  mensualidad: 'Mensualidad',
  uniforme: 'Uniforme',
  torneo: 'Torneo',
  otro: 'Otro',
};

const metodoLabels = {
  pago_movil: 'Pago Móvil',
  transferencia: 'Transferencia',
  efectivo_bs: 'Efectivo Bs',
  efectivo_usd: 'Efectivo USD',
  zelle: 'Zelle',
};

const meses = [
  '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const PagoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pago, setPago] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRechazo, setShowRechazo] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [imageZoom, setImageZoom] = useState(false);

  useEffect(() => {
    loadPago();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPago = async () => {
    try {
      setLoading(true);
      const response = await pagosAPI.getById(id);
      setPago(response.data);
    } catch (err) {
      setError('Error cargando el pago');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    try {
      setActionLoading(true);
      await pagosAPI.confirmar(id);
      loadPago();
    } catch (err) {
      setError('Error al confirmar el pago');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) {
      setError('Debe indicar el motivo del rechazo');
      return;
    }
    try {
      setActionLoading(true);
      await pagosAPI.rechazar(id, motivoRechazo);
      setShowRechazo(false);
      setMotivoRechazo('');
      loadPago();
    } catch (err) {
      setError('Error al rechazar el pago');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleString('es-VE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatMonto = (monto) => {
    if (!monto) return '0.00';
    return parseFloat(monto).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lsfc-blue-600"></div>
      </div>
    );
  }

  if (error && !pago) {
    return (
      <div className="bg-red-50 border border-red-300 text-red-700 px-6 py-4 rounded-lg">
        {error}
        <Link to="/admin/pagos" className="ml-4 underline">Volver a pagos</Link>
      </div>
    );
  }

  if (!pago) return null;

  const estado = estadoConfig[pago.estado] || estadoConfig.pendiente;
  const comprobanteUrl = pago.comprobante_url
    ? `${API_URL.replace('/api', '')}/uploads/documentos/${pago.comprobante_url}`
    : null;

  return (
    <div>
      {/* Image zoom modal */}
      {imageZoom && comprobanteUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setImageZoom(false)}
        >
          <button
            className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-50"
            onClick={() => setImageZoom(false)}
          >
            ✕
          </button>
          <img
            src={comprobanteUrl}
            alt="Comprobante ampliado"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Detalle del Pago #{pago.id}</h1>
          <p className="text-gray-500 text-sm mt-1">Registrado el {formatDateTime(pago.created_at)}</p>
        </div>
        <Link to="/admin/pagos" className="text-lsfc-blue-600 hover:text-lsfc-blue-800 font-medium text-sm">
          ← Volver a pagos
        </Link>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
          <button onClick={() => setError('')} className="ml-2 font-bold">✕</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Payment info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status banner */}
          <div className={`${estado.color} border rounded-xl p-4 flex items-center gap-3`}>
            <span className="text-2xl">{estado.icon}</span>
            <div>
              <p className="font-bold text-lg">{estado.label}</p>
              {pago.estado === 'confirmado' && pago.fecha_confirmacion && (
                <p className="text-sm opacity-80">Confirmado el {formatDateTime(pago.fecha_confirmacion)}</p>
              )}
              {pago.estado === 'rechazado' && pago.notas && (
                <p className="text-sm opacity-80">Motivo: {pago.notas}</p>
              )}
            </div>
          </div>

          {/* Payment details card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <h2 className="font-bold text-gray-800">Información del Pago</h2>
            </div>
            <div className="p-4 divide-y divide-gray-100">
              {/* Player */}
              <div className="py-3 flex justify-between">
                <span className="text-sm text-gray-500">Jugador</span>
                <span className="font-medium text-gray-800">
                  {pago.jugador_nombres} {pago.jugador_apellidos}
                </span>
              </div>

              {/* Representante */}
              <div className="py-3 flex justify-between">
                <span className="text-sm text-gray-500">Representante</span>
                <span className="font-medium text-gray-800">
                  {pago.representante_nombre} {pago.representante_apellido}
                </span>
              </div>

              {/* Concept */}
              <div className="py-3 flex justify-between">
                <span className="text-sm text-gray-500">Concepto</span>
                <span className="font-medium text-gray-800">
                  {conceptoLabels[pago.concepto] || pago.concepto}
                  {pago.concepto === 'mensualidad' && pago.mes_correspondiente && (
                    <span className="text-gray-400"> • {meses[pago.mes_correspondiente]} {pago.ano_correspondiente}</span>
                  )}
                </span>
              </div>

              {/* Description */}
              {pago.descripcion && (
                <div className="py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Descripción</span>
                  <span className="font-medium text-gray-800">{pago.descripcion}</span>
                </div>
              )}

              {/* Method */}
              <div className="py-3 flex justify-between">
                <span className="text-sm text-gray-500">Método de pago</span>
                <span className="font-medium text-gray-800">
                  {metodoLabels[pago.metodo_pago] || pago.metodo_pago}
                </span>
              </div>

              {/* Amount */}
              <div className="py-3 flex justify-between items-start">
                <span className="text-sm text-gray-500">Monto</span>
                <div className="text-right">
                  <p className="font-bold text-green-600 text-lg">${formatMonto(pago.monto_usd)} USD</p>
                  <p className="text-sm text-gray-500">Bs. {formatMonto(pago.monto_bs)}</p>
                  {pago.tasa_bcv && (
                    <p className="text-xs text-gray-400">Tasa BCV: {parseFloat(pago.tasa_bcv).toFixed(2)}</p>
                  )}
                </div>
              </div>

              {/* Date */}
              <div className="py-3 flex justify-between">
                <span className="text-sm text-gray-500">Fecha del pago</span>
                <span className="font-medium text-gray-800">{formatDate(pago.fecha_pago)}</span>
              </div>

              {/* Reference */}
              {pago.referencia && (
                <div className="py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Nro. Referencia</span>
                  <span className="font-mono font-bold text-gray-800">{pago.referencia}</span>
                </div>
              )}

              {/* Bank */}
              {pago.banco_origen && (
                <div className="py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Banco origen</span>
                  <span className="font-medium text-gray-800">{pago.banco_origen}</span>
                </div>
              )}

              {/* Phone */}
              {pago.telefono_origen && (
                <div className="py-3 flex justify-between">
                  <span className="text-sm text-gray-500">Teléfono origen</span>
                  <span className="font-medium text-gray-800">{pago.telefono_origen}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons for pending */}
          {pago.estado === 'pendiente' && (
            <div className="bg-white rounded-xl shadow-lg p-4">
              {!showRechazo ? (
                <div className="flex gap-3">
                  <button
                    onClick={handleConfirmar}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>✅ Confirmar Pago</>
                    )}
                  </button>
                  <button
                    onClick={() => setShowRechazo(true)}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold text-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    ❌ Rechazar
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Motivo del rechazo *
                  </label>
                  <textarea
                    value={motivoRechazo}
                    onChange={(e) => setMotivoRechazo(e.target.value)}
                    placeholder="Ej: Comprobante borroso, referencia no coincide, monto incorrecto..."
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none h-24 focus:ring-2 focus:ring-red-300 focus:border-red-400"
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowRechazo(false); setMotivoRechazo(''); }}
                      disabled={actionLoading}
                      className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleRechazar}
                      disabled={actionLoading || !motivoRechazo.trim()}
                      className="flex-1 py-2 bg-red-600 text-white rounded-lg font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {actionLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <>❌ Confirmar Rechazo</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right column - Comprobante */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <h2 className="font-bold text-gray-800">Comprobante</h2>
            </div>
            <div className="p-4">
              {comprobanteUrl ? (
                <div>
                  <img
                    src={comprobanteUrl}
                    alt="Comprobante de pago"
                    className="w-full rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setImageZoom(true)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '';
                      e.target.alt = 'Error cargando imagen';
                      e.target.className = 'hidden';
                      e.target.parentElement.innerHTML = '<div class="text-center py-8"><span class="text-4xl">🖼️</span><p class="text-sm text-gray-500 mt-2">Error cargando imagen</p></div>';
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Haz clic en la imagen para ampliar
                  </p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <span className="text-4xl">📄</span>
                  <p className="text-sm text-gray-500 mt-2">Sin comprobante adjunto</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {pago.notas && pago.estado !== 'rechazado' && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-4 bg-gray-50 border-b">
                <h2 className="font-bold text-gray-800">Notas</h2>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600">{pago.notas}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagoDetalle;
